import React, { useEffect, useState } from 'react';
import cn from 'classnames';
import { format } from 'date-fns';
import { AuthAccountData } from '@models/auth-account-data';
import { AuthPlatform } from '@support/jwt';
import IntegrationIcons from '@components/Icons/IntegrationIcons';
import Modal from '@components/Modals';
import { EmailIcon, XIcon } from '@components/Icons';
import { getExternalAuthURL, openUrlOutsideElectron } from '@support/electron-external';
import { apiRequest } from '@support/api';
import AddEmailModal from './AddEmailModal';
import {
  Section,
  SettingsSectionBox,
  SettingsSectionLabel,
  SettingsSectionRow,
} from '@views/Settings/components';
import DropdownMenu from '@components/Menus/DropdownMenu';
import styles from './AuthAccounts.module.scss';
import componentStyles from '../../../components/SettingsComponents.module.scss';
import { useTheme } from '@support/hooks/use-theme';

const ProviderIcon = ({ provider }: { provider: AuthPlatform }) => {
  return (
    <div className={styles.ProviderIcon}>
      {provider === 'apple' && <IntegrationIcons.apple width={11} height={11} />}
      {provider === 'google' && <IntegrationIcons.googleAlt width={10} height={10} />}
    </div>
  );
};

enum ModalId {
  ErrorLastAccount,
  AddAccount,
  AddAccountError,
  AddEmail,
}

interface VerifiedEmail {
  id: number;
  email: string;
  verified: boolean;
  primary: boolean;
}

interface ErrorModalProps {
  open?: boolean;
  onClose: () => void;
  message: string;
  title?: string;
}

function ErrorModal({ open = false, message, title, onClose }: ErrorModalProps) {
  return (
    <Modal className="modal-small" isOpen={open} onRequestClose={onClose}>
      {title && <div className="modal-header">{title}</div>}
      <div className="modal-content-padded">{message}</div>
      <div className={cn('modal-content-padded', styles.modalActions)}>
        <button type="button" className="btn btn-primary" onClick={onClose}>
          Continue
        </button>
      </div>
    </Modal>
  );
}

export default function AuthAccounts() {
  const [theme] = useTheme();
  const [accounts, setAccounts] = useState<AuthAccountData[]>([]);
  const [verifiedEmails, setVerifiedEmails] = useState<VerifiedEmail[]>([]);
  const [activeModal, setActiveModal] = useState<ModalId | null>(null);
  const [addAccountError, setAddAccountError] = useState<string | null>(null);
  const authMethodCount = accounts.length + verifiedEmails.length;

  useEffect(() => {
    const abortController = new AbortController();
    fetchAccounts(abortController.signal);
    fetchVerifiedEmails(abortController.signal);

    if (window.location.hash.startsWith('#sign-in-accounts')) {
      const searchParams = new URLSearchParams(window.location.hash.split('?')[1]);
      if (searchParams.has('error')) {
        setAddAccountError(searchParams.get('error'));
        setActiveModal(ModalId.AddAccountError);
      }
    }

    return () => {
      abortController.abort();
    };
  }, []);

  useEffect(() => {
    if (window.app.isElectron && window.desktopIntegrations) {
      const abortController = new AbortController();

      const handleErr = (event) => {
        if (typeof event !== 'string' && event.type && event.type === 'auth') {
          setAddAccountError(event.error);
          setActiveModal(ModalId.AddAccountError);
        }
      };

      const handleSuccess = () => {
        fetchAccounts(abortController.signal);
      };

      const unsub1 = window.desktopIntegrations.onConnection(handleSuccess);
      const unsub2 = window.desktopIntegrations.onConnectionError(handleErr);

      return () => {
        unsub1();
        unsub2();
        abortController.abort();
      };
    }

    return () => {};
  }, []);

  async function fetchAccounts(signal: AbortSignal): Promise<void> {
    try {
      const data = await apiRequest<AuthAccountData[]>('GET', '/users/self/auth/');
      if (signal.aborted) {
        return;
      }

      setAccounts(data);
    } catch (e) {
      console.error(e);
    }
  }

  async function fetchVerifiedEmails(signal: AbortSignal): Promise<void> {
    try {
      const verifiedEmailsList = await apiRequest<VerifiedEmail[]>(
        'GET',
        '/verified-emails/',
        undefined,
        { apiVersion: 'v2' },
      );
      if (!signal.aborted && verifiedEmailsList?.length > 0) {
        setVerifiedEmails(verifiedEmailsList);
      }
    } catch (e) {
      console.error(e);
    }
  }

  const confirmAccountDeletion = (onConfirm) => {
    window.app.confirm(
      {
        title: 'Remove Sign In Account',
        message:
          'Are you sure you want to remove this sign in account? You will no longer be able to sign in to Clay with this account.',
      },
      (confirmed) => {
        if (confirmed) {
          onConfirm();
        }
      },
    );
  };

  function disconnectClick(accountId: number): void {
    const accountIndex = accounts.findIndex((a) => a.id === accountId);
    if (accountIndex < 0) {
      console.warn('Could not remove account - id not found:', accountId);
      return;
    }

    const account = accounts[accountIndex];
    confirmAccountDeletion(() => {
      apiRequest<void>(
        'DELETE',
        `/users/self/connections/${account.id}/`,
        account.scope
          ? {
              scope: account.scope.id,
            }
          : undefined,
      )
        .then(() => {
          setAccounts(accounts.filter((a) => a.id !== account.id));
        })
        .catch((err) => {
          if (err.status && err.status === 400) {
            setActiveModal(ModalId.ErrorLastAccount);
            return;
          }
          console.error(err);
        });
    });
  }

  function disconnnectVerifiedEmail(emailId: number) {
    confirmAccountDeletion(() => {
      const verifiedEmailIdx = verifiedEmails.findIndex(({ id }) => id == emailId);
      const originalVerifiedEmails = verifiedEmails.slice();
      setVerifiedEmails(
        verifiedEmails
          .slice(0, verifiedEmailIdx)
          .concat(verifiedEmails.slice(verifiedEmailIdx + 1)),
      );

      apiRequest<void>('DELETE', `/verified-emails/${emailId}/`, undefined, {
        apiVersion: 'v2',
      }).catch((err) => {
        setVerifiedEmails(originalVerifiedEmails);
        console.error(err);
      });
    });
  }

  function accountTitle(account: AuthAccountData): string {
    if (account.email) {
      return account.email;
    }
    const dateString = format(new Date(account.date), 'MMM. do yyyy');
    return `${account.provider} (${dateString})`;
  }

  function closeModal(): void {
    setActiveModal(null);
  }

  async function addAccount(provider: AuthPlatform): Promise<void> {
    if (activeModal === ModalId.AddAccount) {
      setActiveModal(null);
    }
    const { redirect } = await getExternalAuthURL(
      provider,
      window.app.isElectron ? 'desktop' : 'web',
      'auth',
      '/settings#sign-in-accounts',
      'auth-connect',
    );
    if (window.app.isElectron) {
      openUrlOutsideElectron(redirect);
    } else {
      window.location.href = redirect;
    }
  }

  return (
    <Section>
      <SettingsSectionLabel>Sign In Accounts</SettingsSectionLabel>
      <SettingsSectionBox>
        {accounts.map((account) => (
          <SettingsSectionRow
            key={`${account.provider}_${account.id}`}
            className={styles.authAccountRow}
          >
            <ProviderIcon provider={account.provider} />
            <div
              className={cn(styles.accountTitle, {
                [styles.accountApple]: account.provider === 'apple',
              })}
            >
              {accountTitle(account)}
            </div>
            {authMethodCount > 1 && (
              <button
                type="button"
                className={styles.disconnect}
                onClick={() => disconnectClick(account.id)}
              >
                <XIcon width={12} height={12} />
              </button>
            )}
          </SettingsSectionRow>
        ))}

        {verifiedEmails.map((verifiedEmail) => (
          <SettingsSectionRow key={verifiedEmail.id} className={styles.authAccountRow}>
            <div className={styles.ProviderIcon}>
              <EmailIcon />
            </div>
            <div className={styles.accountTitle}>{verifiedEmail.email}</div>
            {authMethodCount > 1 && (
              <button
                type="button"
                className={styles.disconnect}
                onClick={() => disconnnectVerifiedEmail(verifiedEmail.id)}
              >
                <XIcon width={12} height={12} />
              </button>
            )}
          </SettingsSectionRow>
        ))}
      </SettingsSectionBox>

      <DropdownMenu
        menuClassName={styles.addAccountDropdownMenu}
        triggerClassName={cn(
          componentStyles.button,
          componentStyles[theme],
          componentStyles.noAnimation,
        )}
        triggerProps={{ children: 'Add Account' }}
        options={[
          {
            title: 'Add Apple',
            action: () => addAccount('apple'),
          },
          {
            title: 'Add Google',
            action: () => addAccount('google'),
          },
          {
            title: 'Add Email',
            action: () => setActiveModal(ModalId.AddEmail),
          },
        ]}
      />
      {activeModal === ModalId.AddEmail && <AddEmailModal closeModal={closeModal} open={true} />}
      <ErrorModal
        onClose={closeModal}
        open={activeModal === ModalId.ErrorLastAccount}
        title="Can't Disconnect"
        message="You can not delete your only remaining auth account."
      />
      <ErrorModal
        onClose={closeModal}
        title={'Error adding sign in account'}
        message={addAccountError}
        open={activeModal === ModalId.AddAccountError}
      />
    </Section>
  );
}
